import { useStatsigClient } from '@statsig/react-bindings';
import { nanoid } from 'nanoid';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { sleep } from '../../helpers/api-helpers';
import {
  useModalStackActions,
  useModalStackState,
} from '../modal/ModalStackContext';
import NPSSurvey, { NPSSurveyProps } from './NPSSurvey';
import styles from './NPSSurvey.module.css';

interface NPSSurveyContext {
  /** Function to activate a new NPS survey that the user should complete */
  activateNPS: (npsSurvey: NPSSurveyProps) => void;
}
export const NPSSurveyContext = React.createContext<
  NPSSurveyContext | undefined
>(undefined);

/** Builds a context provider for asynchronously opening NPS modals */
function NPSSurveyContextProvider({
  children,
}: React.PropsWithChildren<any>): JSX.Element {
  const { location } = useHistory();

  const { checkGate } = useStatsigClient();
  const hideNPSPopUp = checkGate('hide_nps_popups');

  const [incompleteSurvey, setIncompleteSurvey] = useState<
    NPSSurveyProps | undefined
  >(getIncompleteSurveyInStorage());
  const surveyRef = useRef<NPSSurveyProps | undefined>(incompleteSurvey);

  const modalActions = useModalStackActions();
  const modalState = useModalStackState();

  useEffect(() => {
    if (getIncompleteSurveyInStorage()) {
      setIncompleteSurvey(getIncompleteSurveyInStorage());
    }
  }, [location.pathname]);

  /* eslint-disable react-hooks/exhaustive-deps */
  // disabled eslint warning because modalActions can cause issues if used in dependency array
  useEffect(() => {
    if (hideNPSPopUp || !incompleteSurvey || !modalActions) return;

    setIncompleteSurveyInStorage(incompleteSurvey);
    surveyRef.current = incompleteSurvey;

    sleep(1500).then(() => {
      if (surveyRef.current && modalState.empty) {
        const survey = surveyRef.current;
        surveyRef.current = undefined;
        const modalId = nanoid();
        const content = (
          <NPSSurvey
            {...survey}
            onComplete={() => {
              setIncompleteSurveyInStorage(undefined);
              modalActions.pop(modalId);
            }}
          />
        );
        modalActions.push({
          id: modalId,
          content,
          closeOnBackgroundClick: false,
          className: styles['modal-content'],
        });
      }
    });
  }, [incompleteSurvey, hideNPSPopUp]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <NPSSurveyContext.Provider value={{ activateNPS: setIncompleteSurvey }}>
      {children}
    </NPSSurveyContext.Provider>
  );
}

const LOCAL_STORAGE_KEY = 'NPSSurveyManager.incomplete';

/**
 * Gets the incomplete NPS survey properties from local storage if any exist
 * @returns Incomplete NPS survey properties from local storage if any exist
 */
function getIncompleteSurveyInStorage(): NPSSurveyProps | undefined {
  const incompleteSurvey = localStorage.getItem(LOCAL_STORAGE_KEY);
  return incompleteSurvey ? JSON.parse(incompleteSurvey) : undefined;
}

/**
 * Sets an incomplete NPS survey properties into local storage
 *
 * If `undefined` is passed in, deletes the incomplete survey key altogether from local storage
 * */
function setIncompleteSurveyInStorage(
  npsSurvey: NPSSurveyProps | undefined,
): void {
  if (npsSurvey) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(npsSurvey));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
}

export default NPSSurveyContextProvider;
